import { Popover } from '../Overlay/Overlay.tsx';

export interface MetricTooltipProps {
  title: string;
  /** Already formatted for display (`41.2 %`, `1 204`). */
  value: string;
  /** What the number means, in words. */
  formula: string;
  /** The formula as counts, e.g. `unique(push_open_direct) ÷ unique(accepted_by_provider)`. */
  expression: string;
  /** Attribution window, when the metric has one. */
  window?: string;
  /** Why the number may look odd (e.g. no sends in the period). */
  explanation?: string;
  labels?: { info?: (title: string) => string; window?: string };
}

/**
 * A metric value with its formula one click away (BB-132, DEC-017): every number on a report says how it is counted.
 * A popover rather than a hover tooltip, so the formula is reachable by keyboard and touch.
 */
export function MetricTooltip({ title, value, formula, expression, window, explanation, labels }: MetricTooltipProps) {
  const info = labels?.info?.(title) ?? `How "${title}" is calculated`;
  return (
    <span className="bb-metric">
      <span className="bb-metric__value">{value}</span>
      <Popover
        label={title}
        trigger={
          <button type="button" className="bb-metric__info" aria-label={info}>
            <span aria-hidden="true">i</span>
          </button>
        }
      >
        <div className="bb-metric__details">
          <strong>{title}</strong>
          <p>{formula}</p>
          <code className="bb-metric__expression">{expression}</code>
          {window ? (
            <p>
              {labels?.window ?? 'Window'}: {window}
            </p>
          ) : null}
          {explanation ? <p className="bb-metric__explanation">{explanation}</p> : null}
        </div>
      </Popover>
    </span>
  );
}
